import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Input,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FaRegCommentDots } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { useAuth } from "../../../context/AuthContext";
import {
  getConsultantConversations,
  createConsultantConversation,
  updateConsultantConversation,
  deleteConsultantConversation,
} from "../../../api/consultantsApi";
import ConfirmDialog from "../../../components/Portal/ConfirmDialog";
import { Panel, MiniIconButton, EmptyState } from "../../../components/Portal/ui";

const ConsultantConversationsTab = ({ consultantId }) => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const { selectedBusinessUnitId } = useBusinessUnit();
  const { user: authUser } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formContent, setFormContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  // Delete state
  const [deletingId, setDeletingId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const buOpts = { buId: selectedBusinessUnitId || undefined };

  const fetchConversations = useCallback(async () => {
    if (!consultantId) return;
    setLoading(true);
    try {
      const res = await getConsultantConversations(consultantId, buOpts);
      setConversations(res || []);
    } catch {
      toast({
        title: t("pilotConsultants.detail.conversations.toasts.fetchFailed", {
          defaultValue: "Failed to load conversations",
        }),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [consultantId, selectedBusinessUnitId, toast, t]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleCreate = async () => {
    if (!formContent.trim()) return;
    setSubmitting(true);
    try {
      await createConsultantConversation(
        consultantId,
        {
          content: formContent.trim(),
        },
        buOpts
      );
      toast({
        title: t("pilotConsultants.detail.conversations.toasts.created", {
          defaultValue: "Interaction added",
        }),
        status: "success",
      });
      setFormContent("");
      setShowForm(false);
      fetchConversations();
    } catch {
      toast({
        title: t("pilotConsultants.detail.conversations.toasts.createFailed", {
          defaultValue: "Could not add interaction",
        }),
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (convId) => {
    if (!editContent.trim()) return;
    setEditSubmitting(true);
    try {
      await updateConsultantConversation(
        consultantId,
        convId,
        {
          content: editContent.trim(),
          ...(editDate ? { contactDate: new Date(editDate).toISOString() } : {}),
        },
        buOpts
      );
      toast({
        title: t("pilotConsultants.detail.conversations.toasts.updated", {
          defaultValue: "Interaction updated",
        }),
        status: "success",
      });
      setEditingId(null);
      fetchConversations();
    } catch {
      toast({
        title: t("pilotConsultants.detail.conversations.toasts.updateFailed", {
          defaultValue: "Could not update interaction",
        }),
        status: "error",
      });
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId || deleteLoading) return;
    setDeleteLoading(true);
    try {
      await deleteConsultantConversation(consultantId, deletingId, buOpts);
      toast({
        title: t("pilotConsultants.detail.conversations.toasts.deleted", {
          defaultValue: "Interaction deleted",
        }),
        status: "success",
      });
      setDeletingId(null);
      fetchConversations();
    } catch {
      toast({
        title: t("pilotConsultants.detail.conversations.toasts.deleteFailed", {
          defaultValue: "Could not delete interaction",
        }),
        status: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (v) => {
    if (!v) return null;
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleString();
    } catch {
      return null;
    }
  };

  const toInputDate = (v) => {
    if (!v) return "";
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 16);
    } catch {
      return "";
    }
  };

  const startEdit = (conv) => {
    setEditingId(conv.id);
    setEditContent(conv.content || "");
    setEditDate(toInputDate(conv.contact_date));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent("");
    setEditDate("");
  };

  const getAuthorName = (author) => {
    if (!author) return t("pilotConsultants.detail.conversations.unknownAuthor", { defaultValue: "Unknown" });
    return `${author.first_name || ""} ${author.last_name || ""}`.trim() || author.email || "Unknown";
  };

  const isAuthor = (conv) => {
    // authUser may be the decoded JWT (has user_id string) or the fetched profile (has id number)
    const uid = authUser?.id ?? authUser?.userId;
    return conv.user_id != null && uid != null && String(conv.user_id) === String(uid);
  };

  const addBtn = !showForm ? (
    <Button size="sm" leftIcon={<AddIcon />} variant="primary" onClick={() => setShowForm(true)}>
      {t("pilotConsultants.detail.conversations.actions.add", {
        defaultValue: "Add interaction",
      })}
    </Button>
  ) : null;

  return (
    <Panel
      title={t("pilotConsultants.detail.conversations.title", { defaultValue: "Conversations" })}
      count={loading ? undefined : conversations.length}
      actions={addBtn}
    >
      <Box p="12px">
        {showForm && (
          <Box
            mb="12px"
            p="12px"
            borderWidth="1px"
            borderRadius="10px"
            borderColor="portal.line"
            bg="portal.soft"
          >
            <VStack spacing="8px" align="stretch">
              <Textarea
                size="sm"
                minH="80px"
                resize="vertical"
                fontWeight="600"
                placeholder={t("pilotConsultants.detail.conversations.placeholders.content", {
                  defaultValue: "Describe the interaction...",
                })}
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
              />
              <HStack justify="flex-end" spacing="8px">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setShowForm(false);
                    setFormContent("");
                  }}
                  isDisabled={submitting}
                >
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleCreate}
                  isLoading={submitting}
                  isDisabled={!formContent.trim()}
                >
                  {t("pilotConsultants.detail.conversations.actions.save", {
                    defaultValue: "Save",
                  })}
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}

        {loading ? (
          <Center py="24px">
            <Spinner color="portal.primary" />
          </Center>
        ) : conversations.length === 0 ? (
          <EmptyState
            icon={<FaRegCommentDots />}
            title={t("pilotConsultants.detail.conversations.noConversations", {
              defaultValue: "No conversations yet.",
            })}
          />
        ) : (
          <VStack align="stretch" spacing="10px">
            {conversations.map((conv) => (
              <Box
                key={conv.id}
                borderWidth="1px"
                borderRadius="10px"
                borderColor="portal.line"
                bg="portal.panel"
                p="12px"
              >
                {editingId === conv.id ? (
                  <VStack spacing="8px" align="stretch">
                    <Input
                      type="datetime-local"
                      size="sm"
                      fontWeight="600"
                      value={editDate}
                      onChange={(e) => setEditDate(e.target.value)}
                    />
                    <Textarea
                      size="sm"
                      minH="80px"
                      resize="vertical"
                      fontWeight="600"
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                    />
                    <HStack justify="flex-end" spacing="8px">
                      <Button size="sm" variant="secondary" onClick={cancelEdit} isDisabled={editSubmitting}>
                        {t("common.cancel", { defaultValue: "Cancel" })}
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleUpdate(conv.id)}
                        isLoading={editSubmitting}
                        isDisabled={!editContent.trim()}
                      >
                        {t("pilotConsultants.detail.conversations.actions.save", {
                          defaultValue: "Save",
                        })}
                      </Button>
                    </HStack>
                  </VStack>
                ) : (
                  <>
                    <Flex justify="space-between" align="flex-start" gap="8px">
                      <HStack spacing="8px" align="center" minW={0}>
                        <Avatar
                          size="sm"
                          name={getAuthorName(conv.author)}
                          src={conv.author?.avatar_url}
                        />
                        <Box minW={0}>
                          <Text fontSize="13px" fontWeight="800" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                            {getAuthorName(conv.author)}
                          </Text>
                          <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                            {conv.contact_date
                              ? formatDate(conv.contact_date)
                              : formatDate(conv.created_at)}
                          </Text>
                        </Box>
                      </HStack>
                      {isAuthor(conv) && (
                        <HStack spacing="6px" flexShrink={0}>
                          <Tooltip label={t("pilotConsultants.detail.conversations.actions.edit", { defaultValue: "Edit" })}>
                            <MiniIconButton
                              aria-label={t("pilotConsultants.detail.conversations.actions.edit", { defaultValue: "Edit" })}
                              icon={<EditIcon />}
                              onClick={() => startEdit(conv)}
                            />
                          </Tooltip>
                          <Tooltip label={t("pilotConsultants.detail.conversations.actions.delete", { defaultValue: "Delete" })}>
                            <MiniIconButton
                              aria-label={t("pilotConsultants.detail.conversations.actions.delete", { defaultValue: "Delete" })}
                              icon={<DeleteIcon />}
                              color="portal.danger"
                              _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                              onClick={() => setDeletingId(conv.id)}
                            />
                          </Tooltip>
                        </HStack>
                      )}
                    </Flex>
                    <Text fontSize="13px" color="portal.text" mt="8px" whiteSpace="pre-wrap" sx={{ marginBottom: "0 !important" }}>
                      {conv.content}
                    </Text>
                  </>
                )}
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      <ConfirmDialog
        isOpen={!!deletingId}
        onClose={() => setDeletingId(null)}
        onConfirm={handleDelete}
        title={t("pilotConsultants.detail.conversations.confirmDeleteTitle", {
          defaultValue: "Delete interaction?",
        })}
        body={t("pilotConsultants.detail.conversations.confirmDeleteBody", {
          defaultValue: "This action cannot be undone.",
        })}
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
      />
    </Panel>
  );
};

export default ConsultantConversationsTab;
